import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): "light" | "dark" {
  if (typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

function getInitialTheme(): Theme {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("theme") as Theme | null;
    return stored || "light";
  }
  return "light";
}

// Apply theme immediately to prevent flash
function applyThemeToDocument(theme: Theme) {
  const root = window.document.documentElement;
  const resolved = theme === "system" ? getSystemTheme() : theme;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
  return resolved;
}

// Apply theme on script load to prevent flash
if (typeof window !== "undefined") {
  const initialTheme = getInitialTheme();
  applyThemeToDocument(initialTheme);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => 
    theme === "system" ? getSystemTheme() : theme
  );
  const [isHydrated, setIsHydrated] = useState(false);

  // Apply theme to HTML element
  useEffect(() => {
    const resolved = applyThemeToDocument(theme);
    setResolvedTheme(resolved);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? "dark" : "light";
      const root = window.document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(newTheme);
      setResolvedTheme(newTheme);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  // Load theme from profile AFTER hydration (non-blocking)
  useEffect(() => {
    setIsHydrated(true);
    
    // Defer profile sync to not block initial render
    const syncProfileTheme = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("tema")
            .eq("user_id", session.user.id)
            .single();
          
          if (profile?.tema && profile.tema !== theme) {
            setThemeState(profile.tema as Theme);
            localStorage.setItem("theme", profile.tema);
          }
        }
      } catch (error) {
        // Silently fail - localStorage theme is good enough
        console.error("Failed to sync theme from profile:", error);
      }
    };

    // Use requestIdleCallback or setTimeout to defer
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(syncProfileTheme);
    } else {
      setTimeout(syncProfileTheme, 100);
    }

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        try {
          const { data: profile } = await supabase
            .from("profiles")
            .select("tema")
            .eq("user_id", session.user.id)
            .single();
          
          if (profile?.tema) {
            setThemeState(profile.tema as Theme);
            localStorage.setItem("theme", profile.tema);
          }
        } catch (error) {
          console.error("Failed to sync theme on sign in:", error);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setTheme = useCallback(async (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);

    // Sync with database in background (non-blocking)
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await supabase
          .from("profiles")
          .update({ tema: newTheme })
          .eq("user_id", session.user.id);
      }
    } catch (error) {
      console.error("Failed to sync theme to profile:", error);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
